import { api } from ".";

/** Normalized payer id (+ optional display name from API). */
export interface IContractModelingPayerRef {
  id: number;
  name: string;
}

function normalizePayersWithDataResponse(
  response: unknown
): IContractModelingPayerRef[] {
  if (response == null) return [];

  let raw: unknown = response;
  if (typeof raw === "object" && raw !== null && "data" in raw) {
    raw = (raw as { data: unknown }).data;
  }

  if (!Array.isArray(raw) || raw.length === 0) return [];

  const first = raw[0];
  if (typeof first === "number") {
    return (raw as number[]).map((id) => ({ id, name: "" }));
  }
  if (typeof first === "string") {
    return (raw as string[])
      .map((s) => {
        const id = parseInt(s, 10);
        return Number.isNaN(id) ? null : { id, name: "" };
      })
      .filter((p): p is IContractModelingPayerRef => p !== null);
  }

  return (raw as unknown[])
    .map((row): IContractModelingPayerRef | null => {
      if (row == null || typeof row !== "object") return null;
      const r = row as Record<string, unknown>;
      const idRaw = r.payerId ?? r.id ?? r.payer_id;
      if (idRaw == null) return null;
      const id =
        typeof idRaw === "string" ? parseInt(idRaw, 10) : Number(idRaw);
      if (!Number.isFinite(id)) return null;
      const nameRaw = r.payerName ?? r.name ?? r.payer_name ?? "";
      const name = typeof nameRaw === "string" ? nameRaw : "";
      return { id, name };
    })
    .filter((p): p is IContractModelingPayerRef => p !== null);
}

export interface IContractModelingAnalysisRequest {
  primaryPayerId: number;
  comparisonPayerIds: number[];
  cptCodes?: string[];
  startDate?: string;
  endDate?: string;
}

export interface IContractModelingComparisonCell {
  payerId: number;
  payerName: string;
  avgRate: number;
  modeledRevenue: number;
  rateChangePercent: number;
}

export interface IContractModelingRow {
  cptCode: string;
  volume: number;
  primaryPayer: {
    avgRate: number;
    revenue: number;
  };
  comparisonPayers: IContractModelingComparisonCell[];
  revenuePotential: number;
}

export interface IContractModelingComparisonTotal {
  payerId: number;
  payerName: string;
  totalModeledRevenue: number;
}

export interface IContractModelingAnalysisResponse {
  rows: IContractModelingRow[];
  totals: {
    primaryPayerRevenue: number;
    comparisonPayerRevenues: IContractModelingComparisonTotal[];
    revenuePotential: {
      current: number;
      potential: number;
      difference: number;
      avgRateChangePercent: number;
    };
  };
}

const contractModelingQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getContractModelingPayersWithData: builder.query<
      IContractModelingPayerRef[],
      void
    >({
      query: () => "/contract-modeling/payers-with-data",
      transformResponse: (response: unknown) =>
        normalizePayersWithDataResponse(response),
      providesTags: ["ContractInsights"],
    }),
    getContractModelingAvailableCptCodes: builder.query<
      string[],
      {
        payerId?: number;
        startDate?: string;
        endDate?: string;
      } | void
    >({
      query: (params) => ({
        url: "/contract-modeling/available-cpt-codes",
        params: params || {},
      }),
      transformResponse: (response: { data: string[] }) => response.data,
      providesTags: ["ContractInsights"],
    }),
    postContractModelingAnalysis: builder.mutation<
      IContractModelingAnalysisResponse,
      IContractModelingAnalysisRequest
    >({
      query: (body) => ({
        url: "/contract-modeling/analysis",
        method: "POST",
        body,
      }),
      transformResponse: (response: {
        data: IContractModelingAnalysisResponse;
      }) => response.data,
    }),
  }),
});

export const {
  useGetContractModelingPayersWithDataQuery,
  useGetContractModelingAvailableCptCodesQuery,
  usePostContractModelingAnalysisMutation,
} = contractModelingQueries;
